import React, { FormEvent } from 'react';
import { Circle, Timer, Hourglass } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAdminData } from '../../hooks/useAdminData';
import AdminErrorBanner from '../AdminErrorBanner';
import { transcriptTextToParagraphs } from '../../../../utils/formatTranscriptText';
import * as adminApi from '../../../../api/adminApi';
import styles from './TranscriptsSection.module.css';
import metricsStyles from '../statistics/MetricsCards.module.css';
import genStyles from '../../pages/GeneratedTranscriptsPage.module.css';
import { Eyebrow, PageHeading } from '../../../../design-system';

export interface TranscriptsSectionProps {
  error: string | null;
  successMessage: string | null;
  newTranscriptUrl: string;
  newTranscriptLanguage: string;
  transcriptPreview: adminApi.AdminYoutubeTranscriptPreview | null;
  previewLoading: boolean;
  uploadLoading: boolean;
  onUrlChange: (url: string) => void;
  onLanguageChange: (language: string) => void;
  onGenerateTranscript: (event: FormEvent) => Promise<void>;
  onUploadTranscript: () => Promise<void>;
  onClearPreview: () => void;
}

export const TranscriptsSection: React.FC<TranscriptsSectionProps> = ({
  error,
  successMessage,
  newTranscriptUrl,
  newTranscriptLanguage,
  transcriptPreview,
  previewLoading,
  uploadLoading,
  onUrlChange,
  onLanguageChange,
  onGenerateTranscript,
  onUploadTranscript,
  onClearPreview,
}) => {
  const navigate = useNavigate();
  const { transcripts, admins, adminProfile } = useAdminData();

  // Find current admin's id from admins list by matching email
  const currentAdminId = admins.find(a => a.email === adminProfile?.email)?.id;
  const myTranscripts = currentAdminId != null
    ? transcripts.filter(t => t.admin_id === currentAdminId)
    : [];

  const recent = myTranscripts.slice(0, 3);

  const formatStatus = (t: any) => (t.download_url ? 'Completed' : 'Processing');

  const isToday = (dateStr?: string) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  };

  const liveCount = myTranscripts.filter((t) => !t.download_url).length;
  const processedToday = myTranscripts.filter((t) => isToday(t.created_at)).length;
  // rough average processing: for completed transcripts, use age as placeholder
  // compute average processing time only when transcripts include an explicit processing time
  // (backend may provide `processing_minutes` or `processing_seconds`). If not present, show unknown.
  const completed = myTranscripts.filter((t) => t.download_url && t.created_at);
  const processingValues: number[] = completed
    .map((t: any) => {
      if (typeof t.processing_minutes === 'number') return t.processing_minutes;
      if (typeof t.processing_seconds === 'number') return Math.round(t.processing_seconds / 60);
      return null;
    })
    .filter((v) => v !== null) as number[];

  const avgProcessingMinutes = processingValues.length
    ? Math.round(processingValues.reduce((acc, v) => acc + v, 0) / processingValues.length)
    : null;

  return (
    <section className={styles.section}>
      {/* Page header — uses a flow div, NOT the .sectionHeaderMinimal class
          which is a fixed 8px spacer that was clipping the heading. */}
      <div style={{ marginBottom: '1rem' }}>
        <Eyebrow tone="default" dot>TRANSCRIPTS</Eyebrow>
        <PageHeading
          accentWord="Transcripts"
          accentTone="orange"
          size="h2"
          align="left"
          level="h2"
        >
          YouTube Transcripts
        </PageHeading>
      </div>
      <div className={styles.detailCard}>
        {/* KPIs on top */}
        <div className={styles.kpiRow}>
          <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_gold}`}>
            <span className={metricsStyles.metricIconBadge}>T</span>
            <div className={metricsStyles.metricContent}>
              <div className={metricsStyles.metricLabel}>Total Transcripts</div>
              <div className={metricsStyles.metricValue}>{myTranscripts.length}</div>
              <div className={metricsStyles.metricMeta}>All transcripts</div>
            </div>
          </div>

          <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_pink}`}>
            <span className={metricsStyles.metricIconBadge}><Circle size={20} aria-hidden /></span>
            <div className={metricsStyles.metricContent}>
              <div className={metricsStyles.metricLabel}>Live Hearings</div>
              <div className={metricsStyles.metricValue}>{liveCount}</div>
              <div className={metricsStyles.metricMeta}>Currently processing</div>
            </div>
          </div>

          <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_blue}`}>
            <span className={metricsStyles.metricIconBadge}><Timer size={18} aria-hidden /></span>
            <div className={metricsStyles.metricContent}>
              <div className={metricsStyles.metricLabel}>Processed Today</div>
              <div className={metricsStyles.metricValue}>{processedToday}</div>
              <div className={metricsStyles.metricMeta}>Completed in last 24h</div>
            </div>
          </div>

          <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_green}`}>
            <span className={metricsStyles.metricIconBadge}><Hourglass size={18} aria-hidden /></span>
            <div className={metricsStyles.metricContent}>
              <div className={metricsStyles.metricLabel}>Avg. Processing Time</div>
              <div className={metricsStyles.metricValue}>{avgProcessingMinutes ? `${avgProcessingMinutes} min` : '—'}</div>
              <div className={metricsStyles.metricMeta}>Estimated average</div>
            </div>
          </div>
        </div>

        <div className={`${styles.detailContent} ${styles.transcriptContent}`}>
          <form className={styles.transcriptForm} onSubmit={onGenerateTranscript}>
            {error && (
              <AdminErrorBanner
                variant="inline"
                message={error}
                details={
                  error.includes('API key') || error.includes('cloud server')
                    ? 'Contact support to enable YouTube transcript retrieval on cloud.'
                    : 'Verify the YouTube URL or try again shortly.'
                }
              />
            )}
            {successMessage && <p className={styles.successText}>{successMessage}</p>}
            <label className={styles.formLabel}>
              <span>YouTube URL</span>
              <input
                type="url"
                value={newTranscriptUrl}
                onChange={(e) => onUrlChange(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                required
              />
            </label>
            <label className={styles.formLabel}>
              <span>Language (optional)</span>
              <input
                type="text"
                value={newTranscriptLanguage}
                onChange={(e) => onLanguageChange(e.target.value)}
                placeholder="en"
              />
            </label>
            <div className={styles.formActionsRow}>
              <button
                className={`${styles.button} ${styles.compactButton}`}
                type="submit"
                disabled={previewLoading}
              >
                {previewLoading ? 'Generating…' : 'Generate Transcript'}
              </button>
              <button
                type="button"
                className={styles.viewGeneratedBtn}
                onClick={() => navigate('/admin/dashboard/generated-transcripts')}
              >
                View generated transcripts
              </button>
              <button
                type="button"
                className={styles.viewSyncedBtn}
                onClick={() => navigate('/admin/dashboard/synced-docs')}
              >
                View synced docs/transcripts
              </button>
            </div>
          </form>
          <div style={{ width: '100%' }}>
            {/* Transcript preview / upload area */}
            {previewLoading && !transcriptPreview && (
              <div className={styles.transcriptPreviewCard}>Fetching transcript…</div>
            )}
            {transcriptPreview && (
              <div className={styles.transcriptPreviewCard}>
                <div className={styles.transcriptPreviewHeader}>
                  <div>
                    <p className={styles.transcriptPreviewTitle}>
                      {transcriptPreview.title || 'Transcript preview'}
                    </p>
                    <p className={styles.transcriptPreviewMeta}>
                      {transcriptPreview.youtube_url}
                    </p>
                    {transcriptPreview.language && (
                      <p className={styles.transcriptPreviewMeta}>
                        Language: {transcriptPreview.language}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    className={styles.outlineButton}
                    onClick={onClearPreview}
                  >
                    Clear preview
                  </button>
                </div>
                <div className={styles.transcriptPreviewBody}>
                  {transcriptTextToParagraphs(transcriptPreview.transcript_text).map(
                    (paragraph, idx) => (
                      <p key={`transcript-paragraph-${idx}`}>{paragraph}</p>
                    )
                  )}
                </div>
                <div className={styles.transcriptPreviewActions}>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={onUploadTranscript}
                    disabled={uploadLoading}
                  >
                    {uploadLoading ? 'Uploading…' : 'Upload Transcript'}
                  </button>
                </div>
              </div>
            )}

            <div className={styles.recentTableWrap} style={{ marginTop: 12 }}>
              <h3 style={{ margin: '0 0 8px 0' }}>Recent Transcripts</h3>
              <table className={styles.recentTable}>
                <thead>
                  <tr>
                    <th>Hearing Title</th>
                    <th>Source</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.id}>
                      <td className={styles.titleCell}>{r.title}</td>
                      <td className={styles.sourceCell}>{r.youtube_url}</td>
                      <td className={styles.dateCell}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</td>
                      <td className={styles.statusCell}>{formatStatus(r)}</td>
                      <td className={styles.actionsCell}>
                        {r.download_url && (
                          <a href={r.download_url} target="_blank" rel="noopener noreferrer" className={genStyles.linkButton}>Download</a>
                        )}
                        {r.download_url ? (
                          <a href={`/admin/dashboard/generated-transcripts/view?url=${encodeURIComponent(r.download_url)}`} target="_blank" rel="noopener noreferrer" className={genStyles.ghostLink}>Open</a>
                        ) : (
                          <a href={r.youtube_url} target="_blank" rel="noopener noreferrer" className={genStyles.ghostLink}>Open</a>
                        )}
                        <button
                          type="button"
                          className={genStyles.deleteButton}
                          onClick={async () => {
                            if (!window.confirm('Delete transcript?')) return;
                            const token = window.localStorage.getItem('policyora_token');
                            if (!token) {
                              window.alert('Not authenticated');
                              return;
                            }
                            try {
                              await adminApi.deleteAdminYoutubeTranscript(token, r.id);
                              window.location.reload();
                            } catch (err) {
                              console.error('Failed to delete transcript', err);
                              window.alert('Failed to delete transcript');
                            }
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
